// QuestionDisplay.js
import React, { useEffect } from 'react';
import { Bookmark } from 'lucide-react';
import './QuestionDisplay.css';

// Question types constants
export const QUESTION_TYPES = {
  SINGLE: 'single',
  MULTIPLE: 'multiple'
};

const QuestionDisplay = ({
  type = QUESTION_TYPES.SINGLE,
  title,
  instruction,
  script,
  questionText,
  readingText,
  questions = [],
  options = [],
  media = [],
  selectedAnswer,
  selectedAnswers = {},
  isAnswered = false,
  correctAnswer,
  onAnswerSelect,
  isBookmarked = false,
  onBookmarkToggle,
  explanation,
  explanationMedia = [],
  mode = 'solving',
  answersHistory = {},
  currentQuestionId,
  className = '',
  imageUrl,
  category,
  formatText = text => text, // Function for text formatting, default is identity
  debugInfo // Debug information to display
}) => {
  // Effect to restore previous answers in review mode
  useEffect(() => {
    if (mode === 'review' && 
        answersHistory[currentQuestionId] && 
        onAnswerSelect && 
        !isAnswered) {
      if (type === QUESTION_TYPES.SINGLE) {
        const previousAnswer = answersHistory[currentQuestionId].selectedAnswers[0];
        if (previousAnswer !== undefined) {
          onAnswerSelect(previousAnswer);
        }
      } else {
        const previousState = answersHistory[currentQuestionId];
        Object.entries(previousState.selectedAnswers).forEach(([index, answerId]) => {
          onAnswerSelect(parseInt(index), answerId);
        });
      }
    }
  }, [currentQuestionId, mode, answersHistory, isAnswered, onAnswerSelect, type]);

  const handleSingleAnswerSelect = (answerId) => {
    if (mode !== 'review' && !isAnswered) {
      onAnswerSelect(answerId);
    }
  };


  // Render for single questions
  if (type === QUESTION_TYPES.SINGLE) {
    return (
      <div className={`qd-question-display ${className}`}>
        <div className="qd-question-header">
          <div className="qd-question-title">{title || category || "Question 1"}</div>
          
          {onBookmarkToggle && (
            <button 
              className="qd-bookmark-btn" 
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                console.log("QD: Bookmark button clicked");
                console.log("Current state:", isBookmarked);
                console.log("Current question ID:", currentQuestionId);
                
                // Call the parent handler with the current question ID
                if (onBookmarkToggle) {
                  console.log("Calling parent onBookmarkToggle");
                  onBookmarkToggle(currentQuestionId);
                } else {
                  console.error("onBookmarkToggle is not defined!");
                }
              }}
              aria-label={isBookmarked ? "Remove bookmark" : "Add bookmark"}
            >
              <Bookmark size={18} fill={isBookmarked ? "currentColor" : "none"} />
            </button>
          )}
        </div>
        
        {instruction && (
          <div className="qd-general-instruction">
            {formatText(instruction)}
          </div>
        )}

        {script && (
          <div className="qd-question-text">
            {formatText(script)}
          </div>
        )}

        {questionText && (
          <div className="qd-question-text">
            {formatText(questionText)}
          </div>
        )}


        {imageUrl && (
          <div className="qd-image-container">
            <img src={imageUrl} alt="Question" className="qd-question-image" />
          </div>
        )}

        <div className="qd-answer-options">
          {options.map((option, index) => {
            // Handle both array of objects with id/text or simple array of strings
            const optionId = option.id || index;
            const optionText = option.text || option;
            
            return (
              <button
                key={optionId}
                className={`qd-answer-option ${
                  selectedAnswer === optionId ? 'selected' : ''
                } ${
                  isAnswered ? (
                    // Always show correct answer in green
                    optionId === correctAnswer || optionText === correctAnswer ? 'correct' :
                    // Only show user's actual selection as incorrect (if it's not the correct answer)
                    (selectedAnswer === optionId && optionId !== correctAnswer && optionText !== correctAnswer) ? 'incorrect' : ''
                  ) : ''
                } ${mode === 'review' ? 'review-mode' : ''}`}
                onClick={() => handleSingleAnswerSelect(optionId)}
                disabled={isAnswered || mode === 'review'}
              >
                <span className="qd-answer-number">{optionId + 1}</span>
                <span className="qd-answer-text">{formatText(optionText)}</span>
              </button>
            );
          })}
        </div>

        {isAnswered && explanation && (
          <div className="qd-explanation-section">
            <h3 className="qd-explanation-title">Explanation</h3>
            <div className="qd-explanation-text">{formatText(explanation)}</div>
          </div>
        )}
      </div>
    );
  }

  // Render for multiple questions
  return (
    <div className={`qd-question-display qd-multiple ${className}`}>
      <div className="qd-container">
        <div className="qd-content">
          {/* Left reading section */}
          <div className="qd-reading-section">
            <div className="qd-question-header">
              <div className="qd-question-title">{title || category || "Reading Question"}</div>
              {onBookmarkToggle && (
                <button 
                  className="qd-bookmark-btn" 
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onBookmarkToggle(currentQuestionId);
                  }}
                  aria-label={isBookmarked ? "Remove bookmark" : "Add bookmark"}
                >
                  <Bookmark size={18} fill={isBookmarked ? "currentColor" : "none"} />
                </button>
              )}
            </div>
            
            {instruction && (
              <div className="qd-general-instruction">
                {formatText(instruction)}
              </div>
            )}
            
            <div className="qd-text-scrollable">
              {/* Script (texte de lecture) - même design que questions normales */}
              {readingText && (
                <div className="qd-question-text">
                  {formatText(readingText)}
                </div>
              )}
              
              {/* Message d'erreur si contenu manquant */}
              {!readingText && !questionText && (
                <div className="qd-question-text" style={{color: 'red'}}>
                  Contenu manquant
                </div>
              )}
              

              {imageUrl && (
                <div className="qd-image-container">
                  <img src={imageUrl} alt="Question" className="qd-question-image" />
                </div>
              )}
            </div>
          </div>

          {/* Right questions section - Handle multiple questions */}
          <div className="qd-questions-section">
            <div className="qd-questions-scroll">
              {/* Check if we have multiple questions to display */}
              {questions && questions.length > 0 ? (
                // Display each question in the group, sorted by ref_id
                [...questions]
                  .sort((a, b) => {
                    // Sort by ref_id if available (lowest first)
                    if (a.ref_id && b.ref_id) {
                      return parseInt(a.ref_id) - parseInt(b.ref_id);
                    }
                    // Fallback to question id
                    return a.id - b.id;
                  })
                  .map((question, qIndex) => (
                  <div className="qd-question-block" key={question.id} style={{ marginBottom: '30px', borderBottom: qIndex < questions.length - 1 ? '1px solid #eee' : 'none', paddingBottom: '20px' }}>
                    {/* Question text for each question */}
                    <div className="qd-question-text" style={{ background: 'transparent', padding: '16px 16px 0 16px' }}>
                      {formatText(question.text)}
                    </div>

                    {/* Options for each question */}
                    <div className="qd-answer-options" style={{ padding: '0 16px' }}>
                      {question.options.map((option, index) => {
                        // Handle both array of objects with id/text or simple array of strings
                        const optionId = option.id || index;
                        const optionText = option.text || option;
                        const isSelected = selectedAnswers[question.id] === optionId;
                        const isCorrect = isAnswered && (optionId === question.correctAnswer || optionText === question.correctAnswer);
                        // Only mark as incorrect if this was actually selected by the user AND it's not the correct answer
                        const isIncorrect = isAnswered && isSelected && !isCorrect;

                        return (
                          <button
                            key={`${question.id}-${optionId}`}
                            className={`qd-answer-option ${
                              isSelected ? 'selected' : ''
                            } ${
                              isCorrect ? 'correct' :
                              isIncorrect ? 'incorrect' : ''
                            } ${mode === 'review' ? 'review-mode' : ''}`}
                            onClick={() => {
                              // Use the actual index from the sorted array, not from the original array
                              const originalIndex = questions.findIndex(q => q.id === question.id);
                              console.log(`Clicked answer for question ${question.id}, using index ${originalIndex}`);
                              onAnswerSelect(originalIndex, optionId);
                            }}
                            disabled={isAnswered || mode === 'review'}
                          >
                            <span className="qd-answer-number">{optionId + 1}</span>
                            <span className="qd-answer-text">{formatText(optionText)}</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Explanation for each question */}
                    {isAnswered && question.explanation && (
                      <div className="qd-explanation-section">
                        <h3 className="qd-explanation-title">Explanation</h3>
                        <div className="qd-explanation-text">{formatText(question.explanation)}</div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                // For a single question (fallback)
                <div className="qd-question-block">
                  {/* Question text without title or background */}
                  <div className="qd-question-text" style={{ background: 'transparent', padding: '16px 16px 0 16px' }}>
                    {formatText(questionText)}
                  </div>

                  {/* Answer options */}
                  <div className="qd-answer-options" style={{ padding: '0 16px' }}>
                    {options.map((option, index) => {
                      // Handle both array of objects with id/text or simple array of strings
                      const optionId = option.id || index;
                      const optionText = option.text || option;

                      return (
                        <button
                          key={optionId}
                          className={`qd-answer-option ${
                            selectedAnswer === optionId ? 'selected' : ''
                          } ${
                            isAnswered ? (
                              optionId === correctAnswer || optionText === correctAnswer ? 'correct' :
                              selectedAnswer === optionId ? 'incorrect' : ''
                            ) : ''
                          } ${mode === 'review' ? 'review-mode' : ''}`}
                          onClick={() => handleSingleAnswerSelect(optionId)}
                          disabled={isAnswered || mode === 'review'}
                        >
                          <span className="qd-answer-number">{optionId + 1}</span>
                          <span className="qd-answer-text">{formatText(optionText)}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Explanation - SINGLE style */}
                  {isAnswered && explanation && (
                    <div className="qd-explanation-section">
                      <h3 className="qd-explanation-title">Explanation</h3>
                      <div className="qd-explanation-text">{formatText(explanation)}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionDisplay;